import axios, { AxiosError } from 'axios';

// Função para normalizar a URL da API com base no protocolo atual
const getApiBaseURL = (): string => {
    const envApiURL = import.meta.env.VITE_API_URL;

    // Se não estiver definida, usar fallback relativo (funciona com proxy nginx)
    if (!envApiURL) {
        console.warn('⚠️ VITE_API_URL não definida, usando fallback: /api');
        return '/api';
    }

    // Se for URL relativa (começa com /), usar como está (será relativa à origem atual)
    if (envApiURL.startsWith('/')) {
        return envApiURL;
    }

    // Se for URL absoluta, verificar se precisa ajustar o protocolo
    try {
        const url = new URL(envApiURL);
        const currentProtocol = window.location.protocol; // 'http:' ou 'https:'
        const isLocalhost = url.hostname === 'localhost' || url.hostname === '127.0.0.1';

        // NUNCA converter localhost para HTTPS (localhost raramente tem SSL em desenvolvimento)
        if (isLocalhost) {
            const pageHost = window.location.hostname;
            const pageIsLocal = pageHost === 'localhost' || pageHost === '127.0.0.1';
            // Página aberta de OUTRO host (ex.: TV/PC na rede via IP) mas a API
            // aponta para localhost → usa o host da página para alcançar a API
            // (mantém a porta, ex.: 3000). Conserta acesso remoto no dev.
            if (!pageIsLocal) {
                url.hostname = pageHost;
                return url.toString();
            }
            // Se for localhost e estiver configurado como HTTP, manter HTTP mesmo se site estiver em HTTPS
            if (url.protocol === 'http:') {
                return envApiURL; // Manter HTTP para localhost
            }
        }

        // Para URLs não-localhost: Se o site está em HTTPS mas a API está configurada para HTTP, ajustar para HTTPS
        if (!isLocalhost && currentProtocol === 'https:' && url.protocol === 'http:') {
            // Aviso apenas em desenvolvimento e de forma menos verbosa
            if (import.meta.env.DEV) {
                console.debug('🔒 Ajustando protocolo da API de HTTP para HTTPS (site está em HTTPS)');
            }
            url.protocol = 'https:';
            return url.toString();
        }

        // Se o site está em HTTP mas a API está configurada para HTTPS, manter HTTPS (mais seguro)
        if (currentProtocol === 'http:' && url.protocol === 'https:') {
            // Aviso apenas em desenvolvimento e de forma menos verbosa
            if (import.meta.env.DEV) {
                console.debug('🔒 Mantendo HTTPS na API (mais seguro que HTTP)');
            }
        }

        return envApiURL;
    } catch (e) {
        // Se não for uma URL válida, usar como está
        console.warn('⚠️ VITE_API_URL não é uma URL válida, usando como está:', envApiURL);
        return envApiURL;
    }
};

// Validar e normalizar a URL da API
export const apiBaseURL = getApiBaseURL();

// Log da URL final configurada (apenas em desenvolvimento)
if (import.meta.env.DEV) {
    console.log('🔗 API Base URL configurada:', apiBaseURL);
    console.log('🌐 Protocolo atual:', window.location.protocol);
    console.log('📍 Origem atual:', window.location.origin);
}

const api = axios.create({
    baseURL: apiBaseURL,
    timeout: 30000, // 30 segundos - aumentado para operações que podem demorar (ex: criação com validação)
    headers: {
        'Content-Type': 'application/json'
    }
});

// Interceptor para adicionar token JWT e ajustar headers
api.interceptors.request.use(
    (config) => {
        const token = localStorage.getItem('token');
        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
        }
        if (config.data instanceof FormData) {
            delete config.headers['Content-Type'];
        }
        return config;
    },
    (error) => {
        return Promise.reject(error);
    }
);

// Interceptor para tratar erros de resposta
api.interceptors.response.use(
    (response) => {
        return response;
    },
    async (error: AxiosError) => {
        const config = error.config;
        const url = config?.url || '';

        // Multi-tenant: sessão de um tenant usada no domínio de outro (401 tenant_divergente)
        // → logout SEMPRE, mesmo em rota pública — a sessão não vale neste domínio.
        if (
            error.response?.status === 401 &&
            (error.response?.data as { error?: string } | undefined)?.error === 'tenant_divergente'
        ) {
            console.warn('🏢 Sessão de outro tenant detectada (tenant_divergente), encerrando sessão...');
            const { useAuthStore } = await import('@/stores/authStore');
            useAuthStore().logout();
            return Promise.reject(error);
        }

        // Tratamento para erros 401 (Unauthorized) - Token expirado ou inválido
        if (error.response?.status === 401) {
            // Rotas públicas - não fazer logout em 401 (login, check-status, listagem de salas)
            const publicRoutes = ['/auth/login', '/auth/check-status', '/rooms', '/indoor-tv/player', '/separacao/public'];
            const isPublicRoute = publicRoutes.some((route) => url.includes(route));

            // Não fazer logout se for requisição para login ou rota pública
            if (!isPublicRoute) {
                console.warn('🚪 Token expirado ou inválido (401), redirecionando para login...');

                // Importar authStore dinamicamente para evitar dependência circular
                const { useAuthStore } = await import('@/stores/authStore');
                const authStore = useAuthStore();

                // Fazer logout (já inclui redirecionamento para login)
                authStore.logout();

                // Retornar erro para que a requisição seja rejeitada
                return Promise.reject(error);
            }
        }

        // Tratamento silencioso para 404s em rotas de permissões (é esperado quando a rota não tem permissões customizadas)
        // Não rejeitar 404s em rotas de permissões para evitar erros no console do navegador
        if (error.response?.status === 404 && url.includes('/user-route-levels/')) {
            // Marcar como 404 esperado e retornar uma resposta simulada para evitar erro no console
            (error as any).isExpected404 = true;
            // Retornar como resposta 404 válida em vez de erro (já que validateStatus vai tratar)
            return Promise.reject(error);
        }

        // Tratamento silencioso para 404s em rotas de avaliação (é esperado quando não há tentativas)
        // Marcar como 404 esperado para evitar erros no console do navegador
        if (error.response?.status === 404 && url.includes('/evaluation/best-attempt')) {
            (error as any).isExpected404 = true;
            return Promise.reject(error);
        }

        // Tratamento específico para timeout
        if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
            const fullUrl = config ? `${config.baseURL}${config.url}` : 'URL desconhecida';
            console.error('⏱️ Timeout na requisição:', {
                url: fullUrl,
                timeout: config?.timeout,
                baseURL: apiBaseURL,
                method: config?.method?.toUpperCase()
            });

            // Criar erro mais descritivo com timeout real da requisição
            const requestTimeout = config?.timeout || 30000;
            const timeoutError = new Error(
                `Tempo de espera excedido (${requestTimeout / 1000}s). Verifique sua conexão ou se o servidor está respondendo.`
            );
            (timeoutError as any).isTimeout = true;
            (timeoutError as any).originalError = error;
            return Promise.reject(timeoutError);
        }

        // Tratamento para erros de rede (sem conexão)
        if (!error.response && error.request) {
            const fullUrl = config ? `${config.baseURL}${config.url}` : 'URL desconhecida';
            console.error('🌐 Erro de rede:', {
                url: fullUrl,
                baseURL: apiBaseURL,
                message: error.message
            });

            const networkError = new Error(
                `Erro de conexão: Não foi possível conectar ao servidor. Verifique sua conexão com a internet e se o servidor está online.`
            );
            (networkError as any).isNetworkError = true;
            (networkError as any).originalError = error;
            return Promise.reject(networkError);
        }

        return Promise.reject(error);
    }
);

export default api;
